import React, {useCallback, useEffect, useRef} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {observer} from 'mobx-react-lite';

import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import TextInput from '../../components/themed/TextInput';
import ValidationMessage from '../../components/themed/ValidationMessage';
import AddressEntry from '../../components/ui-controls/address/AddressEntry';
import {InputLabel} from '../../components/ui-controls/form/InputLabel';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {PasswordRequirementsIndicator} from '../account/components/PasswordRequirementsIndicator';
import TogglePasswordVisibleIconButton from '../account/components/TogglePasswordVisibleIconButton';
import {WeakPasswordBypass} from '../account/components/WeakPasswordBypass';
import {usePasswordInput} from '../account/hooks';
import {
  rewardsFullSignUpBaseSchema,
  RewardsFullSignUpFormData,
  rewardsFullSignUpWithBypassSchema,
} from '../account/schemas/authSchemas';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TextInputRef = any;

// Ref to track bypass state for dynamic resolver
const bypassResolverBase = zodResolver(rewardsFullSignUpBaseSchema);
const bypassResolverWithBypass = zodResolver(rewardsFullSignUpWithBypassSchema);

const RewardsFullSignUpScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_FULL_SIGN_UP>) => {
    const {firstName} = route.params || {};
    const {rewardsStore, userStore} = useStores();

    // Guard: redirect to rewards sign-in if no email context (e.g. direct URL access)
    const hasEmailContext = !!userStore.emailAddress;
    useEffect(() => {
      if (!hasEmailContext) {
        navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
      }
    }, [hasEmailContext, navigation, rewardsStore.signInRouteParams]);

    const passwordRef = useRef<TextInputRef>(null);
    const confirmPasswordRef = useRef<TextInputRef>(null);

    // Track bypass state in a ref so the resolver can read it without re-mounting the form
    const showBypassRef = useRef(false);

    const form = useForm<RewardsFullSignUpFormData>({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resolver: (values: any, context: any, options: any) => {
        const activeResolver = showBypassRef.current
          ? bypassResolverWithBypass
          : bypassResolverBase;
        return activeResolver(values, context, options);
      },
      defaultValues: {
        password: '',
        confirmPassword: '',
        billingAddress: {
          lines: [],
          locality: '',
          administrative_area: '',
          postal_code: '',
        },
        passwordValidationBypass: false,
      },
      mode: 'onBlur',
    });

    const {
      control,
      handleSubmit,
      formState: {isSubmitting, isSubmitted},
    } = form;

    const tabBarPadding = useTabBarBottomPadding();

    const passwordInput = usePasswordInput({
      form,
      confirmFieldName: 'confirmPassword',
      passwordRef,
      confirmPasswordRef,
    });

    // Keep ref in sync with hook state
    showBypassRef.current = passwordInput.showWeakPasswordCheckbox;

    const onSubmit = useCallback(
      async (data: RewardsFullSignUpFormData) => {
        // Check password strength before submission (unless bypassed)
        if (!data.passwordValidationBypass) {
          let passwordIsOkResponse: {password_is_ok?: boolean} | null = null;

          try {
            /* eslint-disable @typescript-eslint/no-explicit-any */
            passwordIsOkResponse = (await apiService.checkPasswordStrength({
              password: data.password,
              email: userStore.emailAddress,
              billingAddress: data.billingAddress,
            } as any)) as any;
            /* eslint-enable @typescript-eslint/no-explicit-any */
          } catch (error: unknown) {
            const axiosError = error as {
              response?: {
                data?: {
                  error?: {
                    errors?: Array<{code?: string; detail?: string}>;
                  };
                };
              };
            };

            const apiErrors = axiosError?.response?.data?.error?.errors;
            const weakErr = apiErrors?.find(err => err.code === 'sign_up_form_password_too_weak');

            passwordInput.handleWeakPasswordError({
              status: 'PASSWORD_TOO_WEAK',
              message: weakErr?.detail || '',
            });
            return;
          }

          if (!passwordIsOkResponse?.password_is_ok) {
            passwordInput.handleWeakPasswordError({
              status: 'PASSWORD_TOO_WEAK',
              message: '',
            });
            return;
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await userStore.rewardsFullSignup(data as any);
      },
      [passwordInput, userStore],
    );

    if (!hasEmailContext) return null;

    return (
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps="always"
        enableOnAndroid
        extraScrollHeight={40}
        contentContainerStyle={{paddingBottom: tabBarPadding}}>
        <ContentView>
          <Text variant="heading1">
            Hey {firstName || 'there'}! Let&apos;s finish creating your account!
          </Text>

          <Text style={{marginBottom: 20}}>
            Enter your details below for full access to online shopping!
          </Text>

          <View style={{gap: 8, zIndex: 1000}}>
            <Controller
              control={control}
              name="password"
              render={({field: {onChange, onBlur, value}, fieldState: {isTouched}}) => (
                <>
                  <TextInput
                    autoCapitalize="none"
                    value={value || ''}
                    label={<InputLabel required>Password</InputLabel>}
                    onBlur={() => {
                      passwordInput.passwordFieldHandlers.onBlur();
                      onBlur();
                    }}
                    onFocus={passwordInput.passwordFieldHandlers.onFocus}
                    onChangeText={(text: string) => {
                      onChange(text);
                      passwordInput.passwordFieldHandlers.onChangeText(text);
                    }}
                    onSubmitEditing={passwordInput.passwordFieldHandlers.onSubmitEditing}
                    ref={passwordRef}
                    secureTextEntry={!passwordInput.isPasswordVisible}
                    touched={isTouched || isSubmitted}
                    errorMessage={undefined}
                    errorMessageWhenTouched={undefined}
                    renderErrorMessage={false}
                    rightIconContainerStyle={{
                      position: 'absolute',
                      right: 10,
                    }}
                    rightIcon={
                      <TogglePasswordVisibleIconButton {...passwordInput.toggleButtonProps} />
                    }
                  />
                  <PasswordRequirementsIndicator {...passwordInput.indicatorProps} />
                </>
              )}
            />

            <WeakPasswordBypass {...passwordInput.bypassCheckboxProps} />

            <Controller
              control={control}
              name="confirmPassword"
              render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => {
                const passwordsMatch = value && passwordInput.passwordValue === value;
                const showError = value && !passwordsMatch;

                return (
                  <TextInput
                    autoCapitalize="none"
                    value={value || ''}
                    label={<InputLabel required>Confirm password</InputLabel>}
                    onBlur={onBlur}
                    onChangeText={(text: string) => {
                      onChange(text);
                      passwordInput.confirmFieldHandlers.onChangeText(text);
                    }}
                    ref={confirmPasswordRef}
                    secureTextEntry={!passwordInput.isConfirmPasswordVisible}
                    touched={isTouched || isSubmitted}
                    errorMessage={showError ? 'Passwords do not match' : undefined}
                    errorMessageWhenTouched={!showError ? error?.message : undefined}
                    rightIconContainerStyle={{
                      position: 'absolute',
                      right: 10,
                    }}
                    rightIcon={
                      <TogglePasswordVisibleIconButton
                        {...passwordInput.confirmToggleButtonProps}
                      />
                    }
                  />
                );
              }}
            />

            <View style={{marginTop: 8}}>
              <Text variant="body2" style={{marginBottom: 8}}>
                <InputLabel required>Billing Address</InputLabel>
              </Text>
              <Controller
                control={control}
                name="billingAddress"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <>
                    <AddressEntry
                      testID="billingAddress"
                      onChange={address => {
                        onChange(address);
                        setTimeout(() => onBlur(), 0);
                      }}
                      address={
                        value || {lines: [], locality: '', administrative_area: '', postal_code: ''}
                      }
                      placeholder=""
                      // DIS-1592: lite signup already collected the suburb, so
                      // pre-select it in manual entry. Seeds manual entry only —
                      // seeding the form value would make the picker render a
                      // street-less address as though it were selected.
                      initialSuburb={rewardsStore.suburb ?? undefined}
                    />
                    <View style={{minHeight: 30}}>
                      {!!((isTouched || isSubmitted) && error) && (
                        <ValidationMessage>Please enter a valid billing address</ValidationMessage>
                      )}
                    </View>
                  </>
                )}
              />
            </View>
          </View>

          <View style={{gap: 8, marginTop: 40}}>
            <Button
              title={'FINISH SIGNING UP'}
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsFullSignUpScreen;
